import {
  MutableRefObject,
  PropsWithChildren,
  RefObject,
  createContext,
  createRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import GeneralError from '../components/atoms/GeneralError';
import LoadingIndicator from '../components/atoms/LoadingIndicator';
import additionalPaymentConfig from '../components/molecules/PaymentOptions/additionalPaymentConfig';
import EmptyBasket from '../components/organisms/EmptyBasket';
import PaymentConfirmationPending from '../components/organisms/PaymentConfirmationPending';
import { Package, Promotion } from '../types/basket';
import { Country, config } from '../utils/config';
import callApi from './callApi';
import getMutations from './getMutations';
import redirect from './redirect';
import { pushErrorTrackingEventFromErrorObject } from './tracking';
import useBasketNotifications from './useBasketNotifications';
import useGenericNotifications from './useGenericNotifications';
import useIsMobileDevice from './useIsMobileDevice';
import usePolling from './usePolling';
import usePrevious from './usePrevious';

export type Customer = {
  authentication: { data: any; type: string };
  data: {
    birthDate?: string;
    customData?: any;
    email: string;
    firstName: string;
    gender: string;
    id: number;
    lastName: string;
    trackingHash?: string;
    publicKey: string;
    referenceKey?: string;
    title?: string;
    type: string;
    phone?: { plainText: string };
  };
  flags?: {
    canUpgradeToFullAccount?: boolean;
    hasFreeShipping?: boolean;
    isGuest: boolean;
    hasReturnCosts?: boolean;
    isCarrierSelectionBlocked?: boolean;
    hasCodPaymentFees?: boolean;
  };
  groups?: string[];
};

export type Campaign = { isApplicable: boolean; name: string };
export type Newsletter = { customerIsConfirmedSubscriber: boolean; enabled: boolean };

type Merchant = {
  id: number;
  isMarketplaceMerchant: boolean;
  legalNotice?: LegalNotice;
};

export type LegalNotice = {
  city?: string;
  country?: CountryVAT;
  email?: string;
  legalName?: string;
  managingDirectors?: string;
  parentCompanyLegalName?: string;
  parentCompanyRegisterNumber?: string;
  parentCompanyRegistryCourtCity?: string;
  parentCompanyRegistryCourtType?: Record<string, string>;
  registerNumber?: string;
  registryCourtCity?: string;
  registryCourtType?: Record<string, string>;
  representative?: string;
  shippingMerchantName?: string;
  streetWithNumber?: string;
  telephone?: string;
  vatIds?: VatId[];
  zip?: string;
};

type VatId = {
  country?: CountryVAT;
  vatId: string;
};

type CountryVAT = {
  id: number;
  iso3166Alpha2Code: string;
  iso3166Alpha3Code: string;
};

export type PaymentOption = {
  paymentOptionKey: string;
  paymentOptionProvider: string;
  isDisabled: boolean;
  configuration?: {
    initializationData?: any;
    isRecurring?: boolean;
    isAutoCapture?: boolean;
    deferral?: { autoApplyDeferral: boolean; isSelected: boolean; isSupported: boolean; weeks: number };
    fee?: number;
    codPaymentFee?: number;
    instalments?: { number: number; isSelected: boolean }[];
    public?: { requiresTranslation?: boolean; requiresBrands?: string[] };
  };
  instalment?: { firstInstalmentValue: number; numberOfInstalments: number; subsequentInstalmentValue: number };
  notifications?: Notification[];
  serviceCosts?: { total: number };
};

export type RecurringPaymentOption = PaymentOption & {
  recurringPaymentOptionId: string;
  recurringPaymentInformation?: {
    creditcardBrand?: string;
    maskedCreditcardNumber?: string;
    customerEmail?: string;
  };
};

export type GiftCard = RecurringPaymentOption & {
  recurringPaymentInformation: {
    giftCardCode: string;
    remainingAmount: number;
  };
};

export type DeliveryOption = {
  carrierGroupKey: string;
  carrierKey: string;
  deliveryOptionKey: string;
  shippingOptionKey: string;
  hideDivergingBillingAddress?: boolean;
  imageFileName?: string;
  collectionPointType?: string;
  isDisabled?: boolean;
  returnCosts?: {
    minBruttoOrderValueAfterReturns: number;
    returnServiceCost: number;
  };
  deliveryDate?: string;
  deliveryDates?: { maximum: string; minimum: string };
  serviceCosts?: { total: number };
  deliveryEstimations?: { deliveryDateTime: { iso8601: string }; deliveryDays: { maximum: number; minimum: number } };
  requiresDateSchedule?: boolean;
  requiresTimeSchedule?: boolean;
};

export type PaymentOptions = {
  generic: PaymentOption[];
  giftCards?: GiftCard[];
  recurring?: RecurringPaymentOption[];
};

export type ShippingOptions = {
  collectionPointDelivery?: DeliveryOption[];
  homeDelivery?: DeliveryOption[];
  onlineDelivery?: DeliveryOption[];
};

export type OrderOptions = { payment?: PaymentOptions; shipping?: ShippingOptions };

export type SimpleAddress = {
  street: string;
  houseNumber?: string;
  zipCode?: string;
  city: string;
  state?: string;
};

export type BaseAddress = SimpleAddress & {
  country: { iso2Code: Country };
  title?: string;
  salutationCode?: 'f' | 'm' | 'd' | 'n';
  firstName: string;
  lastName: string;
  additional?: string;
  phone?: string;
  forwardToCollectionPoint?: boolean;
};

export type Address = BaseAddress & {
  id: number;
  hash: string;
};

export type CollectionPointAddressPart = {
  collectionPoint?: { customerId?: any; description: string; id: string; type: string };
};

export type ShippingAddress = Address & CollectionPointAddressPart;

export type AddressSuggestion = BaseAddress & {
  isOriginal: boolean;
  signature: string;
};

export type Voucher = {
  discount: number;
  id: string;
  isAppliedAutomatically: boolean;
  name: string;
  type: string;
  value: number;
  code: string;
};

export type PromotionDiscount = {
  id: string;
  name: string;
  discount: number;
};

export type Basket = {
  packages: Package[];
  tax: { amount: number };
  id: string;
  total: {
    costWithTaxWithoutVouchersWithoutServiceCosts: number;
    costPaymentServiceCosts: number;
    costShippingServiceCosts: number;
    costWithTaxWithVouchersWithServiceCosts: number;
    costCapture: number;
    costWithTaxWithVouchersWithServiceCostsWithGiftCard: number;
    costWithTaxWithVouchersWithServiceCostsWithoutGiftCard: number;
    costWithTaxWithVouchersWithoutServiceCosts: number;
    costWithTaxWithoutAbsoluteVouchersWithoutServiceCosts: number;
    costWithTaxWithoutPromotion: number;
    itemGroups?: Record<
      string,
      {
        brutto: number;
        bruttoDiscount: number;
        bruttoDiscountWithoutMainProduct: number;
        bruttoWithoutMainProduct: number;
        netto: number;
        nettoWithoutMainProduct: number;
        tax: number;
        taxWithoutMainProduct: number;
      }
    >;
  };
  promotionEngineValidationError?: boolean;
  serviceCosts?: { key: string; value: number; netValue?: number }[];
  discount: { promotion: number };
  vouchers?: Voucher[];
  pricingEstimations?: {
    items: any[];
    totals: {
      duties: {
        localCurrency: {
          amount: number;
          currency: string;
        };
        originalCurrency: {
          amount: number;
          currency: string;
        };
      };
      taxes: {
        localCurrency: {
          amount: number;
          currency: string;
        };
        originalCurrency: {
          amount: number;
          currency: string;
        };
      };
      totalFees: {
        localCurrency: {
          amount: number;
          currency: string;
        };
        originalCurrency: {
          amount: number;
          currency: string;
        };
      };
    };
  };
};

export type OrderState = {
  id?: number;
  addresses?: { billing?: Address; shipping?: ShippingAddress };
  basket: Basket;
  memberships?: { activated?: ActiveMembership[]; suggestions: Membership[] };
  payment?: { giftCards?: GiftCard[]; selected?: (PaymentOption & Partial<RecurringPaymentOption>)[] };
  // TODO Waiting for BE fix: should be logisticsContact?: { phone: { plainText: string } };
  shipping?: { logisticsContact?: { plainText: string }; selected?: DeliveryOption[] };
  vouchers?: Voucher[];
  promotions?: Promotion[];
  promotionDiscounts?: PromotionDiscount[];
  promotionDiscountTotalAbsolute?: number;
  taxId?: string;
};

export type Membership = { membershipTypeKey: string; earnings: { points: number } };
export type ActiveMembership = Membership & {
  membershipAccountNumber: string;
  totalPointBalance: number;
  redeemablePointBalance: number;
  redeemablePointBalanceInCurrency: number;
  redeemedPoints?: number;
  redeemedPointsInCurrency?: number;
  remainingPointBalance?: number;
};

export type RuleEngine = {
  option: string;
  isMatched: boolean;
  key: string;
  action: string;
  definition: string;
  ruleId?: number;
  condition?: string;
  name?: string;
  description?: string;
  affectedOptions?: string[];
  optionsAfterTheAction?: string[];
};

export type Notification = {
  id?: string;
  type: 'success' | 'info' | 'warning' | 'error';
  textTranslationKey?: string;
  text?: string;
  replacements?: Record<string, any>;
  titleTranslationKey?: string;
};

export type Integrations = { campaigns?: Campaign[]; newsletter: Newsletter; promotions?: { enabled: boolean } };

export type Redirect = { method: 'GET' | 'POST'; url: string; parameters?: { name: string; value: string }[] };

export type CheckoutState = {
  addressSuggestions?: { suggestions: AddressSuggestion[] };
  customer?: Customer;
  integrations: Integrations;
  merchants?: Merchant[];
  orderOptions?: OrderOptions;
  orderState: OrderState;
  debug?: { ruleEngine?: RuleEngine[] };
  notifications?: Notification[];
  orderConfirmation?: { redirect?: Redirect; initializationData?: any };
};

type Mutations = ReturnType<typeof getMutations>;

type CheckoutStateContext = {
  checkoutState: CheckoutState;
  setBasketEmpty: (isBasketEmpty: boolean) => void;
  setPollingEnabled: (enabled: boolean) => void;
  isLoading: string;
  setLoading: (newValue: string) => void;
  scrollRefs: { basketRef: RefObject<HTMLDivElement>; navigationRef: RefObject<HTMLDivElement> };
  adyenSpecificationPayloadRef: MutableRefObject<Record<string, unknown> | undefined>;
} & Mutations;

export const LOADING_TYPES = {
  off: '',
  itemRemove: (id: string) => `item:remove-${id}`,
  itemQuantityChange: (id: string) => `quantity:change-${id}`,
  carrierChange: (carrierAndshipping: string) => `carrier:change-${carrierAndshipping}`,
  paymentChange: (paymentOptionKey: string) => `payment:change-${paymentOptionKey}`,
  adyenLoad: 'adyen:load',
  authPublicKeyAndBirthday: 'auth:publicKeyAndBirthday',
  authSetPasswordForCustomerNumber: 'auth:setPasswordForCustomerNumber',
  authLogin: 'auth:login',
  authRegister: 'auth:register',
  authGuest: 'auth:guest',
  shippingSubmit: 'submitting:shipping',
  collectionPointSearch: 'cp:search',
  collectionPointFilterSearch: (type?: string) => `cp:search${type ? `-${type}` : ''}`,
  billingSame: 'billing:sameAddress',
  billingDifferent: 'billing:differentAddress',
  paymentSubmit: 'submitting:payment',
  loyaltyRegister: 'loyalty:register',
  loyaltyAdd: 'loyalty:add',
  loyaltyRemove: 'loyalty:remove',
  loyaltyRedeem: 'loyalty:redeem',
  loyaltyUnredeem: 'loyalty:unredeem',
  giftcardAdd: 'giftcard:add',
  giftcardRemove: (id: string) => `giftcard:remove-${id}`,
  voucherAdd: 'voucher:add',
  voucherRemove: (id: string) => `voucher:remove-${id}`,
  installmentNumberChange: (value: number) => `installment:number-${value}`,
  installmentDeferralChange: 'installment:deferral',
  citySuggestionsGet: 'cities:suggest',
  streetSuggestionsGet: 'streets:suggest',
  promotionRemove: (id: string) => `promotion:remove-${id}`
};

export let setBasketId: (newBasketId: string) => void | Promise<void>;

const CheckoutStateContext = createContext<CheckoutStateContext>({} as any);

type CheckoutStateProviderProps = {
  initialCheckoutState?: CheckoutState;
  isInitialBasketEmpty: boolean;
  isWaitingForPaymentConfirmation?: boolean;
  initialRequestError?: Error;
  reloadInitialCheckoutState: (newBasketId?: string) => Promise<CheckoutState>;
  onUpdate: (checkoutState: CheckoutState) => void;
  jwt?: string;
};

export const CheckoutStateProvider = (props: PropsWithChildren<CheckoutStateProviderProps>) => {
  const { initialCheckoutState, initialRequestError, reloadInitialCheckoutState, onUpdate, children, jwt } = props;

  const [checkoutState, setCheckoutState] = useState<CheckoutState | undefined>(initialCheckoutState);
  const scrollRefs = { basketRef: createRef<HTMLDivElement>(), navigationRef: createRef<HTMLDivElement>() };
  const adyenSpecificationPayloadRef = useRef();

  useEffect(() => {
    if (initialRequestError || !checkoutState) {
      return;
    }
    onUpdate(checkoutState);
  }, [checkoutState]);

  const previousCheckoutState = usePrevious(checkoutState);
  const [isBasketEmpty, setBasketEmpty] = useState(props.isInitialBasketEmpty);
  const previousIsBasketEmpty = usePrevious(isBasketEmpty);
  const [isLoading, setLoading] = useState<string>('');

  const { triggerBasketUpdateNotification, triggerBasketEmptyNotification } = useBasketNotifications();
  const { triggerGenericNotifications } = useGenericNotifications();

  const isMobile = useIsMobileDevice();

  const pollCheckoutState = useCallback(async () => {
    try {
      const newState = await callApi<CheckoutState>({
        endpoint: '/state',
        failedStatusCodes: [204],
        sequential: true
      });
      triggerBasketUpdateNotification(newState.orderState.basket, previousCheckoutState.current?.orderState.basket);
      setCheckoutState(newState);
    } catch (e: any) {
      if ([204, 404, 410].includes(e.statusCode)) {
        setBasketEmpty(true);
        setPollingEnabled(false);
        if (!previousIsBasketEmpty.current) {
          triggerBasketEmptyNotification();
        }
        return;
      } else if (e.statusCode === 401) {
        return;
      }
      console.error(e);
      pushErrorTrackingEventFromErrorObject(e, 'poll_checkout_state');
    }
  }, []);

  const { initializePolling, setPollingEnabled } = usePolling(pollCheckoutState, config.statePollInterval);
  const mutations = getMutations(checkoutState, setCheckoutState, initializePolling, jwt);

  useEffect(() => {
    if (!props.isWaitingForPaymentConfirmation && !props.isInitialBasketEmpty) {
      initializePolling();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('pageshow', event => {
      if (event.persisted) {
        console.log('This page was restored from the bfcache.');
        setPollingEnabled(true);
      }
    });
  }, []);

  setBasketId = async (newBasketId: string) => {
    try {
      const newState = await reloadInitialCheckoutState(newBasketId);
      setCheckoutState(newState);
    } catch (e: any) {
      if (e.statusCode === 401) {
        return;
      }
      console.error(e);
      pushErrorTrackingEventFromErrorObject(e, 'set_basket_id');
    }
  };

  if (initialRequestError) {
    return <GeneralError error={initialRequestError} />;
  }

  if (isBasketEmpty || (checkoutState?.orderState && !checkoutState.orderState.basket?.packages?.length)) {
    return <EmptyBasket />;
  }

  if (props.isWaitingForPaymentConfirmation) {
    return <PaymentConfirmationPending />;
  }

  if (!checkoutState) {
    return <LoadingIndicator />;
  }

  const paymentOptionKey =
    checkoutState.orderState?.payment?.selected?.[0]?.paymentOptionKey ??
    checkoutState.orderState?.payment?.giftCards?.[0]?.paymentOptionKey;

  if (
    checkoutState.orderConfirmation?.redirect &&
    (!paymentOptionKey ||
      (additionalPaymentConfig[paymentOptionKey]?.modal !== 'always' &&
        (additionalPaymentConfig[paymentOptionKey]?.modal !== 'desktopOnly' || isMobile)))
  ) {
    redirect(checkoutState.orderConfirmation.redirect);
    window.scrollTo({ top: 0 });
    return <LoadingIndicator />;
  }

  triggerGenericNotifications(checkoutState.notifications);

  return (
    <CheckoutStateContext.Provider
      value={{
        checkoutState,
        ...mutations,
        setBasketEmpty,
        setPollingEnabled,
        isLoading,
        setLoading,
        scrollRefs,
        adyenSpecificationPayloadRef
      }}
    >
      {children}
    </CheckoutStateContext.Provider>
  );
};

export const useCheckoutState = () => useContext(CheckoutStateContext);
